import { COMPACT_HEADER_STYLE } from '../../lib/gameChrome'
import React, { useState } from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BlockablePlayer, BlockedUsersResponse, ReportMessageContext } from '@warcarp/api-types'
import { apiGet, apiPost, apiDelete } from '../../lib/api'
import { useAuth } from '../../hooks/useAuth'
import { useContactSubmit } from '../../hooks/useContact'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { useToast } from '../ui/Toast'
import { goBackOr } from '../../lib/goBack'
import { KeyboardAwareScrollView } from '../ui/KeyboardAwareScrollView'
import { PlayerSafetyForm, type PlayerSafetyChoice } from './PlayerSafetyForm'
import { submitPlayerSearch } from '../../lib/playerSafetySearch'
import { ReportConfirmation, type SafetySubmission } from './ReportConfirmation'
import { BlockingHelp } from './BlockingHelp'

export function PlayerSafetyScreen({ reportOnly = false }: { reportOnly?: boolean }) {
  const router = useRouter()
  const { user } = useAuth()
  const { block, messageId, kind, gameId } = useLocalSearchParams<{ block?: string | string[]; messageId?: string; kind?: string; gameId?: string }>()
  const linkedId = Array.isArray(block) ? block[0] : block
  const [selected, setSelected] = useState<BlockablePlayer | null>(null)
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [confirmation, setConfirmation] = useState<SafetySubmission | null>(null)
  const { showToast } = useToast()
  const client = useQueryClient()
  const key = ['blocked-users', user?.id]
  const blocks = useQuery({ queryKey: key, queryFn: () => apiGet<BlockedUsersResponse>('/api/v1/users/me/blocks'), enabled: !reportOnly })
  const evidence = useQuery({
    queryKey: ['report-message', user?.id, kind, messageId],
    queryFn: () => apiGet<ReportMessageContext>(`/api/v1/contact/message-context?kind=${encodeURIComponent(kind ?? '')}&messageId=${encodeURIComponent(messageId ?? '')}`),
    enabled: !!messageId,
  })
  const linked = useQuery({
    queryKey: ['block-target', linkedId],
    queryFn: () => apiGet<BlockablePlayer>(`/api/v1/users/block-target/${encodeURIComponent(linkedId!)}`),
    enabled: !!linkedId,
  })
  const results = useQuery({
    queryKey: ['block-player-search', user?.id, searchTerm],
    queryFn: () => apiGet<{ results: BlockablePlayer[] }>(`/api/v1/users/block-search?q=${encodeURIComponent(searchTerm)}`),
    enabled: searchTerm.length >= 3,
  })
  const target = selected ?? (linkedId ? linked.data : undefined)
  const alreadyBlocked = !reportOnly && !!target && !!blocks.data?.users.some((p) => p.id === target.id)
  function closePrompt() {
    setSelected(null)
    if (linkedId || messageId) router.setParams({ block: undefined, messageId: undefined, kind: undefined, gameId: undefined })
    change.reset()
    report.reset()
  }
  const change = useMutation({
    mutationFn: ({ id, remove }: { id: string; remove?: boolean }) => remove
      ? apiDelete(`/api/v1/users/me/blocks/${encodeURIComponent(id)}`)
      : apiPost(`/api/v1/users/me/blocks/${encodeURIComponent(id)}`, {}),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: key })
      await client.invalidateQueries({ queryKey: ['block-player-search'] })
      await client.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'games' && query.queryKey[2] === 'dms' })
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  })
  const report = useContactSubmit()
  async function submitActions(choice: PlayerSafetyChoice) {
    if (!target || submitting) return
    setSubmitting(true)
    try {
      const changedBlock = !reportOnly && (evidence.data ? choice.blocked && !alreadyBlocked : choice.blocked !== alreadyBlocked)
      if (changedBlock) await change.mutateAsync({ id: target.id, remove: !choice.blocked })
      if (choice.report) await report.mutateAsync({ reason: 'player-report', reportedUserId: target.id, message: choice.reason,
        reportedMessage: evidence.data ? { kind: evidence.data.kind, messageId: evidence.data.messageId, link: evidence.data.link } : undefined,
        reportedDmConversation: !evidence.data && typeof gameId === 'string' ? {
          gameId, link: `/games/${encodeURIComponent(gameId)}/dm/${encodeURIComponent(target.id)}`,
        } : undefined })
      if (choice.report && evidence.data) await client.invalidateQueries({ queryKey: ['report-message', user?.id, kind, messageId] })
      closePrompt()
      setConfirmation({ username: target.username, reported: choice.report, flagged: choice.report && !!evidence.data,
        blockAction: changedBlock ? (choice.blocked ? 'blocked' : 'unblocked') : null,
        gameId: evidence.data?.gameId ?? (typeof gameId === 'string' && gameId.length <= 128 ? gameId : undefined) })
    } catch { /* Keep the form open for retry. Successful block changes remain saved. */ }
    finally { setSubmitting(false) }
  }
  const busy = submitting || change.isPending || report.isPending
  const showPrompt = !!target || !!linkedId
  const findPlayer = () => submitPlayerSearch(search, searchTerm, busy, setSearchTerm, () => results.refetch())
  if (confirmation) return <SafeAreaView className="flex-1 bg-canvas">
    <KeyboardAwareScrollView contentContainerStyle={{ padding: 16 }}>
      <ReportConfirmation submission={confirmation} onLobby={() => router.dismissTo('/(app)')}
        onTickets={() => router.push('/(app)/settings/inbox')}
        onGame={confirmation.gameId ? () => router.dismissTo(`/(app)/games/${encodeURIComponent(confirmation.gameId!)}`) : undefined} />
    </KeyboardAwareScrollView>
  </SafeAreaView>
  return <SafeAreaView className="flex-1 bg-canvas">
    <View className="flex-row items-center px-4 border-b border-olive gap-3" style={COMPACT_HEADER_STYLE}>
      <TouchableOpacity accessibilityRole="button" accessibilityLabel="Back to settings"
        className="p-1" onPress={() => goBackOr(router, reportOnly ? '/(app)/settings/help' : '/(app)/settings')}>
        <Text className="text-blood text-base font-typed">← Back</Text>
      </TouchableOpacity>
      <Text className="font-stencil text-ink text-xl flex-1">{reportOnly ? 'Report User' : 'Blocked Users'}</Text>
    </View>
    <KeyboardAwareScrollView contentContainerStyle={{ padding: 16, gap: 16 }} keyboardShouldPersistTaps="handled">
      {!!linkedId && linked.isPending && <Text className="text-ink">Loading player…</Text>}
      {linked.isError && !!linkedId && <View className="gap-2"><Text className="text-blood">Could not load this player.</Text><Button title="Retry player lookup" onPress={() => void linked.refetch()} /></View>}
      {target && (target.id === user?.id ? <View><Text className="text-ink">{reportOnly ? 'You cannot report yourself.' : 'You cannot block or report yourself.'}</Text><Button title="Cancel" onPress={closePrompt} /></View> :
        <PlayerSafetyForm key={`${target.id}:${evidence.data?.messageId ?? ''}:${!!evidence.data?.alreadyReported}`} username={target.username} alreadyBlocked={alreadyBlocked} busy={busy}
          canBlock={!reportOnly && target.canBlock} reportOnly={reportOnly}
          messageContext={evidence.data} ready={reportOnly || (!!blocks.data && (!messageId || evidence.data?.authorId === target.id))}
          error={report.error?.message ?? change.error?.message} onSubmit={(choice) => void submitActions(choice)} onCancel={closePrompt} />)}
      {showPrompt && !target && <Button title="Cancel" onPress={closePrompt} />}
      {!!messageId && evidence.isPending && <Text className="text-ink">Loading message…</Text>}
      {!!messageId && evidence.isError && <View><Text className="text-blood">Could not load the reported message.</Text><Button title="Retry message" onPress={() => void evidence.refetch()} /></View>}
      {!showPrompt && <>
      <View className="bg-paper rounded-xl p-4 gap-3">
        <View className="flex-row items-center gap-3">
        <Text className="font-stencil text-ink flex-1">{reportOnly ? 'Find player to report' : 'Find player to block or report'}</Text>
          {!reportOnly && <BlockingHelp />}
        </View>
        <Input accessibilityLabel="Player username" placeholder="Exact player username" value={search} onChangeText={setSearch} autoCapitalize="none" maxLength={20}
          returnKeyType="search" onSubmitEditing={findPlayer} />
        <Button title="Find player" disabled={search.trim().length < 3 || busy} onPress={findPlayer} />
        {results.isFetching && <Text className="text-ink">Searching…</Text>}
        {results.isError && <Text className="text-blood">Could not search players. Try again.</Text>}
        {!!results.data && results.data.results.length === 0 && <Text className="text-ink-soft">No players found.</Text>}
        {results.data?.results.map((p) => <Button key={p.id} title={`@${p.username}`} variant="secondary" disabled={busy} onPress={() => { closePrompt(); setSelected(p) }} />)}
      </View>
      {!reportOnly && !!blocks.data?.users.length && <Text className="font-stencil text-ink">Blocked users</Text>}
      {!reportOnly && blocks.isPending && <Text className="text-ink">Loading blocked users…</Text>}
      {!reportOnly && blocks.isError && <View><Text className="text-blood">Could not load blocked users.</Text><Button title="Retry blocked users" onPress={() => void blocks.refetch()} /></View>}
      {!reportOnly && blocks.data?.users.map((p) => <View key={p.id} className="bg-paper rounded-xl p-4 flex-row items-center gap-3">
        <Text className="font-typed text-ink text-lg flex-1">{p.username}</Text>
        <Button title="Unblock" accessibilityLabel={`Unblock @${p.username}`} variant="secondary" size="sm" disabled={busy}
          onPress={() => change.mutate({ id: p.id, remove: true }, { onSuccess: () => showToast(`@${p.username} unblocked`, 'success') })} />
      </View>)}
      </>}
      {showPrompt && !reportOnly && blocks.isError && <View><Text className="text-blood">Could not load blocked users.</Text><Button title="Retry blocked users" onPress={() => void blocks.refetch()} /></View>}
    </KeyboardAwareScrollView>
  </SafeAreaView>
}
